import { useAuthState } from "@/contexts/auth/auth.provider";
import React, { useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { VERIFY_LOCATION } from "@/graphql/queries/listing";
import css from "@/components/add-v2/.module.scss";
import { createHtmlImageMarker, Map, MapDecorator } from "@/components/map/map";
import { defaultProps } from "@/src/pages/map/daily-rent/[[...params]]";
import Config from "@/common/config";
import Icon from "@/components/icon";
import { dispatchCtxChange, dispatchCtxUiEvent } from "@/components/add-v2/ui";
import { Modal, Spinner } from "@aqar-app/react-ui";
import { Wrapper } from "@googlemaps/react-wrapper";

function getCurrentLocation() {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position);
        },
        (error) => {
          reject(error);
        },
      );
    }
  });
}

function InvalidLocationError({ onCancel, onOk }) {
  return (
    <Modal.ClassicOverlay dir="rtl">
      <div className={css.invalidLocationModal}>
        <div className={css.modalContent}>
          <h2>عقار</h2>
          <p>
            قد تتأخر عملية اصدار الترخيص بسبب أن موقع العقار الذي تحاول إضافته
            غير متواجد في بيانات الهيئة العامة للعقار
          </p>
        </div>
        <button className={css.btn} onClick={onCancel || Modal.close}>
          إلغاء عملية إضافة إعلان
        </button>
        <button className={css.btn} onClick={onOk}>
          المتابعة على أي حال
        </button>
      </div>
    </Modal.ClassicOverlay>
  );
}

export function LocationPickerComponent({ nested, readOnly, value, name }) {
  const { token } = useAuthState();
  const mapRef = useRef(null);

  const [verifyLocation, { loading: verifyLocationLoading }] = useLazyQuery(
    VERIFY_LOCATION,
    {
      context: { token },
    },
  );

  const loading = verifyLocationLoading;
  const [error, setError] = useState(null);

  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.addEventListener("validate", (e) => {
      setError(e?.detail?.error);
    });
  }, []);
  return (
    <div ref={rootRef}>
      <input hidden name={name} />
      <div
        data-readel-only={readOnly}
        className={error ? css.hasError + " " + css.mapWrapper : css.mapWrapper}
      >
        <Wrapper libraries={["marker"]} apiKey={Config.GM_API_KEY}>
          <MapDecorator
            center={defaultProps.center}
            zoom={defaultProps.zoom}
            onLoad={(map) => {
              if (!map) return;
              mapRef.current = map;
              let marker;

              if (value) {
                marker = new google.maps.marker.AdvancedMarkerElement({
                  map,
                  position: value,
                  content: createHtmlImageMarker({
                    src: Config.ICONS_BASE_URL + "addListing-mapMarker.svg",
                    className: css.marker,
                  }),
                });
                map.setOptions({
                  center: value,
                  zoom: 15,
                });
              }

              if (!readOnly)
                map.addListener("click", function (e) {
                  if (marker) marker.setMap(null);
                  marker = new google.maps.marker.AdvancedMarkerElement({
                    map,
                    position: e.latLng,
                    content: createHtmlImageMarker({
                      src: Config.ICONS_BASE_URL + "addListing-mapMarker.svg",
                      className: css.marker,
                    }),
                  });

                  dispatchCtxUiEvent(rootRef.current, {
                    type: "loading",
                    value: true,
                  });

                  const location = e.latLng.toJSON();

                  verifyLocation({
                    variables: {
                      location,
                    },
                  })
                    .then(({ data }) => {
                      if (!data?.Listing?.verifyLocation) {
                        Modal.open(
                          <InvalidLocationError
                            onOk={(e) => {
                              dispatchCtxUiEvent(rootRef.current, {
                                type: "loading",
                                value: false,
                              });

                              dispatchCtxChange(rootRef.current, {
                                name,
                                value: location,
                                nested,
                              });
                              Modal.close(e);
                            }}
                          />,
                        )(e);
                      }
                    })
                    .finally(() =>
                      dispatchCtxUiEvent(rootRef.current, {
                        type: "loading",
                        value: false,
                      }),
                    );
                  setError(null);
                  dispatchCtxChange(rootRef.current, {
                    name,
                    value: location,
                    nested,
                  });
                  if (map.getZoom() < 15)
                    map.setOptions({
                      center: e.latLng,
                      zoom: 15,
                    });
                });
            }}
          />
        </Wrapper>
        {loading && (
          <div className={css.spinner}>
            <Spinner size="large" />
          </div>
        )}
        <button
          onClick={() => {
            getCurrentLocation().then((geolocationPosition) => {
              if (!geolocationPosition) return;

              const { latitude, longitude } = geolocationPosition.coords;

              map.setOptions({
                center: { lat: latitude, lng: longitude },
                zoom: 15,
              });
            });
          }}
          className={css.locateme}
        >
          <Icon name="addListing-marker" width={20} height={20} />
        </button>
      </div>

      <p className={css.error}>{error}</p>
    </div>
  );
}
